import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { homePathFor } from "@/auth/ProtectedRoute";
import { isApiError } from "@/lib/api";
import { Button, Card, CardBody, Input } from "@/components/ui";

/** Owned by the scaffold (auth). Other workers should not need to change this. */
export function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? homePathFor(user.role)} replace />;
  }

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      const signedIn = await login(email.trim(), password);
      navigate(homePathFor(signedIn.role), { replace: true });
    } catch (err) {
      setError(
        isApiError(err) ? err.message : "Could not sign in. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-app px-4">
      <div className="w-full max-w-sm">
        <h1 className="mb-1 text-lg font-semibold tracking-tight text-ink">
          Exam Proctor
        </h1>
        <p className="mb-4 text-xs text-ink-muted">
          Sign in to continue.
        </p>

        <Card>
          <CardBody>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
              className="flex flex-col gap-3"
            >
              <Input
                label="Email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                label="Password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={error}
              />
              <Button
                type="submit"
                variant="primary"
                disabled={submitting}
                className="mt-1 w-full"
              >
                {submitting ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
